import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Textarea } from '../components/Textarea';

export const AgentQueryDetailPage = () => {
  const [privateNotes, setPrivateNotes] = useState('Strong voice, interesting premise. Character development needs work but shows promise. Consider requesting partial.');
  const [showFirstPages, setShowFirstPages] = useState(false);

  // Extract query ID from URL - in real app would use router
  const queryId = window.location.pathname.match(/\/agent\/queries\/(\d+)/)?.[1];

  // Mock data - in real app, would fetch based on queryId
  const query = {
    id: queryId,
    status: 'read',
    receivedDate: '2024-02-05',
    author: {
      name: 'Emma Rodriguez',
      email: 'emma.rodriguez@email.com',
      bio: 'Emma Rodriguez is a debut author from Portland, Oregon. She holds an MFA in Creative Writing from University of Oregon and has published short fiction in various literary journals including The Paris Review and Granta.',
      previousWorks: 'Short stories in The Paris Review, Granta, and Tin House',
      website: 'https://www.emmarodriguez.com',
    },
    manuscript: {
      title: 'The Silent Shore',
      genre: 'Literary Fiction',
      wordCount: 85000,
      completionStatus: 'Complete',
      synopsis: `THE SILENT SHORE is an 85,000-word literary fiction novel set in a small coastal Maine town during the winter of 2019.

When forty-year-old marine biologist Dr. Claire Novak returns to her childhood home to care for her dying mother, she's forced to confront the tragedy that shattered their family twenty years earlier. Her younger sister, Lily, drowned under mysterious circumstances, and Claire has spent two decades burying the truth beneath her work studying the ocean's depths.

But the coastal community hasn't forgotten. As Claire settles into the rhythm of caretaking—managing medications, sitting through uncomfortable silences, watching her mother slip away—old neighbors whisper. Her mother's health aide knows more than she should. And Claire's former best friend, now the town librarian, has kept newspaper clippings from that fateful summer.

When a letter arrives from Lily—postmarked just days after her supposed death—Claire's carefully constructed reality begins to crumble. She discovers that her sister's drowning may not have been the accident everyone believed. As winter storms batter the coast and her mother's time runs short, Claire must decide whether to uncover a truth that could destroy what little remains of her family, or let the past stay buried with the dead.

THE SILENT SHORE explores themes of grief, family secrets, and the stories we tell ourselves to survive. It will appeal to readers of Elizabeth Strout's OLIVE KITTERIDGE and Celeste Ng's LITTLE FIRES EVERYWHERE.`,
      compTitles: ['Olive Kitteridge by Elizabeth Strout', 'Little Fires Everywhere by Celeste Ng', 'The Great Believers by Rebecca Makkai'],
      contentWarnings: 'Death, grief, family trauma',
    },
    queryLetter: `Dear Ms. Johnson,

I am seeking representation for my literary fiction novel, THE SILENT SHORE, complete at 85,000 words.

When forty-year-old marine biologist Dr. Claire Novak returns to her childhood home in coastal Maine to care for her dying mother, she's forced to confront the sister who drowned twenty years earlier—and the family secret that may have killed her.

Set against the backdrop of a harsh New England winter, THE SILENT SHORE explores the complicated bonds between mothers and daughters, the weight of unspoken truths, and the question of whether some mysteries are better left unsolved. As Claire uncovers evidence that her sister's death wasn't the accident everyone believed, she must choose between the comfortable lies that have sustained her family and a truth that could destroy them.

I believe this novel would be a good fit for you based on your interest in character-driven literary fiction with strong sense of place. My short fiction has appeared in The Paris Review, Granta, and Tin House. I hold an MFA in Creative Writing from University of Oregon.

Thank you for your time and consideration. I have included the first ten pages below and would be happy to send the complete manuscript at your request.

Sincerely,
Emma Rodriguez`,
    firstPages: `CHAPTER ONE

The house looked smaller than I remembered. Not in the way childhood places always shrink when you return as an adult, but genuinely diminished, as if grief had compressed it, squeezed out the air and light until only the essential structure remained.

I stood on the sidewalk with my suitcase, January wind cutting through my jacket, and tried to remember the last time I'd been home. Five years? Six? My mother's voice on the phone three days ago had been thin, papery. "The doctors say months, maybe weeks. You should come."

So I'd come.

The walkway to the front door was uneven, buckled by frost heaves and neglect. Paint peeled from the clapboards in long strips, revealing gray wood beneath. The garden my mother had tended obsessively was overgrown with dead stalks, last summer's flowers collapsed into brown mush.

I should have come sooner. The thought arrived with the usual companion: guilt.

The door opened before I could knock. Teresa, the home health aide my mother's insurance had assigned, filled the doorway. She was younger than I expected, maybe thirty, with dark hair pulled back in a practical bun.

"Dr. Novak?" She extended her hand. "Your mother's been waiting."

"How is she?"

Teresa's smile was professional, practiced. "Today is a good day. She's been asking for you."

We both knew what that meant. Good days were becoming rare. Soon there would be no days at all.

I followed Teresa through the house, noting the changes. Furniture rearranged for a wheelchair that sat folded in the corner. A hospital bed in what used to be the living room. The smell of illness—antiseptic and something deeper, more organic—overlaying the familiar scent of this house.

My mother was in the bed, propped up on pillows, looking out at the ocean. She'd always insisted on this view. "I want to see the water," she'd said when they moved the bed. Even dying, she needed that connection.

"Claire." Her voice was stronger than I expected. Her eyes, when she turned them on me, were sharp. "You came."

"Of course I came." I crossed to the bed, unsure whether to hug her, kiss her cheek, take her hand. We weren't a touching family. We'd learned young that some distances couldn't be bridged with physical contact.

She solved the problem by gesturing to the chair beside the bed. "Sit. Tell me about your work. Are the whales behaving?"

I sat. "They're whales. They do what they want."

A thin smile. "Like daughters."

We fell silent, watching the ocean through the window. Gray water, gray sky, the line between them blurred by winter light. Somewhere out there, below the surface, the world I'd spent twenty years studying continued its ancient rhythms. Migration and feeding, birth and death, indifferent to the small dramas playing out on land.

"I suppose you heard," my mother said. "About the letter."

My blood went cold. "What letter?"

She turned to look at me fully then, and I saw something I'd never seen in my mother's face before: fear.

"Teresa found it. In the attic. It was addressed to you, postmarked July 15, 2003." A pause. "Three days after Lily died."

The room tilted. I gripped the arms of the chair.

"That's impossible."

"That's what I said." My mother's hands, skeletal now, plucked at the blanket. "But there it is. Upstairs in your old room. I thought you should read it before—" She didn't finish. Before I die, she meant.

I stood, my legs unsteady. "Where?"

"On your dresser. Teresa left it there this morning."

The stairs creaked as I climbed them, each step a journey back through time. The second floor was colder, less lived-in. My old bedroom door stood open.

The room was exactly as I'd left it when I went to college. Twin bed with the blue comforter. Posters of whales and dolphins. Books about marine biology lining the shelves. And on the dresser, next to my high school yearbook, an envelope.

My name in Lily's handwriting. The stamp was the old kind, the design discontinued years ago. The postmark read: Bar Harbor, ME, July 15, 2003, 2:47 PM.

But Lily had died on July 12, 2003. I'd identified her body myself, had watched them lower her casket into the ground on July 18.

My hands shook as I picked up the envelope. It weighed nothing. Just paper and ink and the impossible promise of my dead sister's words.

I should have opened it immediately. Should have torn into it, desperate for whatever message Lily had left me from beyond the grave. But I knew, with the certainty that comes from years of studying predators and prey, that some envelopes shouldn't be opened. That some truths, once known, can never be unknown.

Instead, I stood there, holding my sister's ghost, and listened to the ocean beating against the shore.`,
  };

  const handlePass = () => {
    if (window.confirm('Are you sure you want to pass on this query? This action cannot be undone.')) {
      window.location.href = `/agent/queries/${queryId}/respond?type=pass`;
    }
  };

  const handleRequest = (type: 'partial' | 'full') => {
    window.location.href = `/agent/queries/${queryId}/respond?type=request&request=${type}`;
  };

  const handleSaveNotes = () => {
    console.log('Saving private notes:', privateNotes);
    window.alert('Private notes saved successfully');
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
        }}>
          <Button
            variant="ghost"
            onClick={() => window.location.href = '/agent/queries'}
          >
            ← Back to Inbox
          </Button>
          <Badge variant={query.status === 'read' ? 'default' : 'warning'}>
            {query.status === 'read' ? 'Read' : 'Unread'}
          </Badge>
        </div>

        {/* Query Letter */}
        <Card style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px',
          }}>
            <div>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '500',
                marginBottom: '8px',
                color: theme.colors.textPrimary,
              }}>
                {query.manuscript.title}
              </h1>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                by {query.author.name}
              </p>
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            marginBottom: '24px',
          }}>
            <Badge variant="primary">{query.manuscript.genre}</Badge>
            <Badge variant="default">{query.manuscript.wordCount.toLocaleString()} words</Badge>
            <Badge variant="success">{query.manuscript.completionStatus}</Badge>
          </div>

          <div style={{
            padding: '20px',
            backgroundColor: theme.colors.backgroundAlt,
            borderRadius: theme.borderRadius.medium,
            fontSize: '15px',
            lineHeight: '1.8',
            color: theme.colors.textPrimary,
            whiteSpace: 'pre-wrap',
            fontFamily: theme.fonts.primary,
          }}>
            {query.queryLetter}
          </div>

          <div style={{
            marginTop: '16px',
            fontSize: '14px',
            color: theme.colors.textSecondary,
          }}>
            Received: {query.receivedDate}
          </div>
        </Card>

        {/* Synopsis */}
        <Card style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Synopsis
          </h2>
          <div style={{
            fontSize: '15px',
            lineHeight: '1.8',
            color: theme.colors.textPrimary,
            whiteSpace: 'pre-wrap',
          }}>
            {query.manuscript.synopsis}
          </div>

          <div style={{
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: `1px solid ${theme.colors.border}`,
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              fontSize: '14px',
            }}>
              <div>
                <strong style={{ color: theme.colors.textPrimary }}>Comp Titles:</strong>
                <ul style={{
                  margin: '8px 0 0 0',
                  paddingLeft: '20px',
                  color: theme.colors.textSecondary,
                }}>
                  {query.manuscript.compTitles.map((title, idx) => (
                    <li key={idx}>{title}</li>
                  ))}
                </ul>
              </div>
              <div>
                <strong style={{ color: theme.colors.textPrimary }}>Content Warnings:</strong>
                <div style={{ marginTop: '8px', color: theme.colors.textSecondary }}>
                  {query.manuscript.contentWarnings}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Author Info */}
        <Card style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            About the Author
          </h2>
          <div style={{
            fontSize: '15px',
            lineHeight: '1.8',
            color: theme.colors.textPrimary,
            marginBottom: '16px',
          }}>
            {query.author.bio}
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            fontSize: '14px',
            color: theme.colors.textSecondary,
          }}>
            <div>
              <strong>Email:</strong> {query.author.email}
            </div>
            <div>
              <strong>Website:</strong>{' '}
              <a href={query.author.website} target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary }}>
                {query.author.website}
              </a>
            </div>
            <div>
              <strong>Previous Works:</strong> {query.author.previousWorks}
            </div>
          </div>
        </Card>

        {/* First Pages */}
        <Card style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              margin: 0,
            }}>
              First Pages
            </h2>
            <Button
              size="small"
              variant="outline"
              onClick={() => setShowFirstPages(!showFirstPages)}
            >
              {showFirstPages ? 'Hide' : 'Show'} First Pages
            </Button>
          </div>

          {showFirstPages && (
            <div style={{
              padding: '24px',
              backgroundColor: theme.colors.backgroundAlt,
              borderRadius: theme.borderRadius.medium,
              fontSize: '15px',
              lineHeight: '1.8',
              color: theme.colors.textPrimary,
              whiteSpace: 'pre-wrap',
              fontFamily: theme.fonts.primary,
              maxHeight: '600px',
              overflow: 'auto',
            }}>
              {query.firstPages}
            </div>
          )}
        </Card>

        {/* Private Notes */}
        <Card style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Private Notes
          </h2>
          <p style={{
            fontSize: '14px',
            color: theme.colors.textSecondary,
            marginBottom: '16px',
          }}>
            These notes are only visible to you
          </p>
          <Textarea
            placeholder="Add your private notes about this query..."
            value={privateNotes}
            onChange={(e) => setPrivateNotes(e.target.value)}
            rows={6}
          />
          <div style={{ marginTop: '16px' }}>
            <Button size="small" variant="outline" onClick={handleSaveNotes}>
              Save Notes
            </Button>
          </div>
        </Card>

        {/* Response Options */}
        <Card style={{
          backgroundColor: theme.colors.primaryLight,
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Response Options
          </h2>
          <p style={{
            fontSize: '15px',
            color: theme.colors.textSecondary,
            marginBottom: '24px',
          }}>
            How would you like to respond to this query?
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
          }}>
            <Button onClick={() => handleRequest('partial')}>
              Request Partial Manuscript
            </Button>
            <Button onClick={() => handleRequest('full')}>
              Request Full Manuscript
            </Button>
            <Button variant="outline" onClick={handlePass}>
              Pass on Query
            </Button>
          </div>
        </Card>
      </main>

      <Footer />
    </div>
  );
};
